import { ComponentType } from 'react';
import { toast } from 'react-hot-toast';

import { Toast } from './Toast';

interface UseToastsHook {
    addToast: (text: string, Icon?: ComponentType<{ width?: number; height?: number }>) => void;
    addLoadingToast: <T>(
        promiseFunction: () => Promise<T>,
        loadingMessage: string,
        successMessage: string | ((result: T) => string),
        errorMessage: string | ((error: unknown) => string),
        options?: { signal?: AbortSignal }
    ) => Promise<T>;
    addErrorToast: (text: string, Icon?: ComponentType<{ width?: number; height?: number }>) => void;
}

export const useToasts = (): UseToastsHook => {
    const addToast = (text: string, Icon?: ComponentType<{ width?: number; height?: number }>) => {
        toast.custom((toast) => (
            <Toast isVisible={toast.visible} Icon={Icon}>
                {text}
            </Toast>
        ));
    };

    const addErrorToast = (text: string, Icon?: ComponentType<{ width?: number; height?: number }>) => {
        toast.custom(
            (toastInstance) => (
                <Toast
                    isVisible={toastInstance.visible}
                    state="error"
                    Icon={Icon}
                    onDismiss={() => {
                        toast.dismiss(toastInstance.id);
                    }}
                >
                    {text}
                </Toast>
            ),
            {
                duration: Infinity,
            }
        );
    };

    const addLoadingToast = async function <T>(
        promiseFunction: () => Promise<T>,
        loadingMessage: string,
        successMessage: string | ((result: T) => string),
        errorMessage: string | ((error: unknown) => string),
        options?: { signal?: AbortSignal }
    ): Promise<T> {
        const toastId = toast.custom(
            (toast) => (
                <Toast isVisible={toast.visible} state="loading">
                    {loadingMessage}
                </Toast>
            ),
            {
                duration: Infinity,
            }
        );

        const operationPromise = promiseFunction();
        let abortHandler: (() => void) | undefined;
        const abortPromise = options?.signal
            ? new Promise<never>((_resolve, reject) => {
                  abortHandler = () => reject(new DOMException('Loading toast cancelled', 'AbortError'));
                  if (options.signal?.aborted) {
                      abortHandler();
                  } else {
                      options.signal?.addEventListener('abort', abortHandler, { once: true });
                  }
              })
            : undefined;
        const loadingPromise = abortPromise ? Promise.race([operationPromise, abortPromise]) : operationPromise;

        return loadingPromise
            .then((value) => {
                const message = typeof successMessage === 'function' ? successMessage(value) : successMessage;

                toast.custom((toast) => (
                    <Toast isVisible={toast.visible} state="success">
                        {message}
                    </Toast>
                ));

                return value;
            })
            .catch((error) => {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    throw error;
                }

                const message = typeof errorMessage === 'function' ? errorMessage(error) : errorMessage;

                toast.custom(
                    (toastInstance) => (
                        <Toast
                            isVisible={toastInstance.visible}
                            state="error"
                            onDismiss={() => {
                                toast.dismiss(toastInstance.id);
                            }}
                        >
                            {message}
                        </Toast>
                    ),
                    {
                        duration: Infinity,
                    }
                );

                throw error;
            })
            .finally(() => {
                if (abortHandler) {
                    options?.signal?.removeEventListener('abort', abortHandler);
                }
                toast.dismiss(toastId);
            });
    };

    return {
        addToast,
        addErrorToast,
        addLoadingToast,
    };
};
